import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import ApiService from 'src/services/apiService'
import { MasterWorkout } from 'src/types/MasterWorkout'
import { MasterWorkoutExercise, MasterWorkoutExerciseLookups } from 'src/types/MasterWorkoutExercise'
import { downloadFile } from 'src/utils/downloadFile'
import MasterWorkoutExerciseViewModel from './MasterWorkoutExerciseViewModel'

export default class MasterWorkoutExerciseListViewModel {
    public masterWorkout: MasterWorkout
    public masterWorkoutExerciseVMs: MasterWorkoutExerciseViewModel[]
    public clipboardWeek: { copiedWeek?: number; masterWorkoutExercises?: MasterWorkoutExercise[] } = {}
    public activeWeek: number = 1
    public activeMasterWorkoutExercise: MasterWorkoutExerciseViewModel
    public lookups: MasterWorkoutExerciseLookups = {
        masterExercises: [],
        groupAccessTerms: [],
        groupFilterTerms: [],
        masterGroups: [],
    }

    // For submitting only modified or deleted data
    public createdOrEditedMasterWorkoutExerciseVMs: Set<MasterWorkoutExerciseViewModel> = new Set()
    public deletedMasterWorkoutExerciseIds: Set<number> = new Set()

    constructor(private service: ApiService) {
        makeObservable<MasterWorkoutExerciseListViewModel>(this, {
            masterWorkout: observable,
            masterWorkoutExerciseVMs: observable.deep,
            clipboardWeek: observable.deep,
            activeWeek: observable,
            activeMasterWorkoutExercise: observable,
            weeks: computed,
            days: computed,
            groupByDayMasterWorkoutExercises: computed,
            fetch: action,
            setActiveWeek: action,
            setMasterWorkout: action,
            setActiveMasterWorkoutExercise: action,
            createMasterWorkoutExercise: action,
            pasteWeek: action,
            updateMasterWorkoutExerciseOrder: action,
            batchEditMasterWorkoutExercises: action,

            // lookups
            lookups: observable,
        })
    }

    get weeks(): number[] {
        return this.masterWorkout ? [...Array(this.masterWorkout?.weeks).keys()].map((i) => i + 1) : []
    }

    get days(): number[] {
        // get the keys from the groupByDayMasterWorkoutExercises map
        // this map is already filtered by the active week, the keys will be strings of the day numbers, e.g., ["2", "4", "5"]
        const dayKeys = Array.from(this.groupByDayMasterWorkoutExercises.keys());

        // convert the string keys to numbers and sort them numerically
        const daysWithExercises = dayKeys.map(Number).sort((a, b) => a - b);

        return daysWithExercises;
    }

    get groupByDayMasterWorkoutExercises(): Map<string, MasterWorkoutExerciseViewModel[]> {
        if (!(this.masterWorkout && this.masterWorkoutExerciseVMs)) return new Map<string, MasterWorkoutExerciseViewModel[]>()

        // filter by activeWeek
        const filteredMasterWorkoutExerciseVMs = this.masterWorkoutExerciseVMs
            .filter((vm) => vm.masterWorkoutExercise.week == this.activeWeek)
            .sort((a, b) => a.masterWorkoutExercise.displayOrder - b.masterWorkoutExercise.displayOrder)

        const grouped = _.groupBy(filteredMasterWorkoutExerciseVMs, 'masterWorkoutExercise.day')

        console.log('@MasterWorkoutExerciseListViewModel -> groupByDayMasterWorkoutExercises', grouped)
        return new Map<string, MasterWorkoutExerciseViewModel[]>(Object.entries(grouped))
    }

    public setMasterWorkout = (masterWorkout: MasterWorkout) => (this.masterWorkout = masterWorkout)

    //public setMasterWorkout = (masterWorkout: MasterWorkout) => {
    //    (this.masterWorkout = masterWorkout)

    //    if (this.masterWorkout?.id) {
    //        this.fetch(this.masterWorkout.id);
    //    }
    //}

    public setActiveMasterWorkoutExercise = (masterWorkoutExerciseVm: MasterWorkoutExerciseViewModel) => {
        console.log('@MasterWorkoutExerciseListViewModel -> setActiveMasterWorkoutExercise', masterWorkoutExerciseVm)
        this.activeMasterWorkoutExercise = masterWorkoutExerciseVm
        this.markForCreateOrEdit(masterWorkoutExerciseVm)
    }

    public createMasterWorkoutExercise = (day: number, displayOrder: number) => {
        const newMasterWorkoutExercise = new MasterWorkoutExerciseViewModel(this.service, {
            week: this.activeWeek,
            day,
            displayOrder,
            masterWorkoutId: this.masterWorkout.id,
        })
        this.masterWorkoutExerciseVMs.push(newMasterWorkoutExercise)
        this.setActiveMasterWorkoutExercise(newMasterWorkoutExercise)
    }

    public fetch = (masterWorkoutId: number | string): Promise<void> => {
        console.log('@MasterWorkoutExerciseListViewModel -> fetch', masterWorkoutId)
        this.setActiveMasterWorkoutExercise(null)
        return this.service.getMasterWorkoutExercises(masterWorkoutId).then((masterWorkoutExercises) => {
            this.masterWorkoutExerciseVMs = masterWorkoutExercises.map((mwe) => new MasterWorkoutExerciseViewModel(ApiService.instance, mwe))
        })
    }

    public getMasterWorkoutExerciseLookups = (): Promise<void> => {
        return this.service.getMasterWorkoutExerciseLookups().then((res) => {
            this.lookups = res
        })
    }

    public setActiveWeek = (week: number): void => {
        this.activeWeek = week
        console.log('@MasterWorkoutExerciseListViewModel -> setActiveWeek', week)
    }

    public deleteMany = (deletedVms: MasterWorkoutExerciseViewModel[]): Promise<void> => {
        console.log(`@MasterWorkoutExerciseListViewModel -> delete count: ${deletedVms.length}`)

        this.setActiveMasterWorkoutExercise(null)

        // remove deleted data from CreatedOrEdited list if any exist
        deletedVms.forEach((vm) => this.createdOrEditedMasterWorkoutExerciseVMs.delete(vm))

        // remove deleted data from main list
        this.masterWorkoutExerciseVMs = this.masterWorkoutExerciseVMs.filter(
            (vm) =>
                !deletedVms.some(
                    (delVm) =>
                        delVm.masterWorkoutExercise.week == vm.masterWorkoutExercise.week &&
                        delVm.masterWorkoutExercise.day == vm.masterWorkoutExercise.day &&
                        delVm.masterWorkoutExercise.displayOrder == vm.masterWorkoutExercise.displayOrder,
                ),
        )

        deletedVms.forEach((delVm) => {
            if (delVm.masterWorkoutExercise.id) this.deletedMasterWorkoutExerciseIds.add(delVm.masterWorkoutExercise.id)
        })

        // Autosubmit
        this.service.deleteMasterWorkoutExercises(Array.from(this.deletedMasterWorkoutExerciseIds))

        // recalculate order after deletion
        this.reorderAffectedDaysMasterWorkoutExercises(deletedVms)
        return
    }

    public clearWeek = (): void => {
        const deletedVms = this.masterWorkoutExerciseVMs.filter((vm) => vm.masterWorkoutExercise.week == this.activeWeek)
        if (deletedVms.length > 0) this.deleteMany(deletedVms)
    }

    public copyWeek = (): void => {
        const clipboard = this.masterWorkoutExerciseVMs.filter((vm) => vm.masterWorkoutExercise.week == this.activeWeek).map((vm) => vm.masterWorkoutExercise)
        this.clipboardWeek = {
            copiedWeek: this.activeWeek,
            masterWorkoutExercises: clipboard,
        }
    }

    public pasteWeek = (): void => {
        // nothing to paste
        if (!this.clipboardWeek.copiedWeek) return

        // Reset ids to insert new data
        const pastedMasterWorkoutExercises = this.clipboardWeek.masterWorkoutExercises.map(
            (mwe) =>
                new MasterWorkoutExerciseViewModel(ApiService.instance, {
                    ...mwe,
                    week: this.activeWeek,
                    id: null,
                    masterWorkoutExerciseSets: mwe.masterWorkoutExerciseSets.map((set) => ({ ...set, id: null })),
                }),
        )

        // clear current week
        this.clearWeek()

        // update master workout exercises list
        this.masterWorkoutExerciseVMs = [...this.masterWorkoutExerciseVMs, ...pastedMasterWorkoutExercises]
        pastedMasterWorkoutExercises.forEach((vm) => this.markForCreateOrEdit(vm))

        // reset clipboard
        this.clipboardWeek = {}

        // Autosubmit
        pastedMasterWorkoutExercises.forEach((vm) => vm.submitMasterWorkoutExercise())
    }

    public exportJson = async (): Promise<void> => {

        const obj = await this.service.getMasterWorkoutForExport(this.masterWorkout.id);

        const json = JSON.stringify(obj)

        const exportedFile = new File([json], `${this.masterWorkout.name}.json`, {
            type: 'text/plain',
        })

        downloadFile(exportedFile)
    }

    //public exportJson = async (): Promise<void> => {
    //    try {
    //        if (this.masterWorkout?.id) {
    //            await this.fetch(this.masterWorkout.id);
    //        }

    //        this.lookups = await this.service.getMasterWorkoutExerciseLookups();
    //        const masterWorkoutLookups = await this.service.getMasterWorkoutLookups();

    //        const updatedMasterWorkoutExercises = this.masterWorkoutExerciseVMs.map(exercise => {
    //            const updatedGroupTermIds = exercise.masterWorkoutExercise.groupTermIds.map(termId => {
    //                const term = this.lookups.groupAccessTerms.find(t => t.value === termId);
    //                return term ? [termId, term.name] : [termId];
    //            });

    //            return {
    //                ...exercise.masterWorkoutExercise,
    //                groupTermIds: updatedGroupTermIds
    //            };
    //        });

    //        // Transform masterWorkoutMaxes
    //        const updatedMasterWorkoutMaxes = this.masterWorkout.masterWorkoutMaxes.map(max => {
    //            const updatedGroupTermIds = max.groupTermIds?.map(termId => {
    //                const term = masterWorkoutLookups.groupTerms.find(t => t.value === termId);
    //                return term ? [termId, term.name] : [];
    //            });

    //            return {
    //                ...max,
    //                groupTermIds: updatedGroupTermIds
    //            };
    //        });

    //        const obj = {
    //            masterWorkout: {
    //                ...this.masterWorkout,
    //                masterWorkoutMaxes: updatedMasterWorkoutMaxes
    //            },
    //            masterWorkoutExercises: updatedMasterWorkoutExercises,
    //        };

    //        const json = JSON.stringify(obj);

    //        const exportedFile = new File([json], `${this.masterWorkout.name}.json`, {
    //            type: 'text/plain',
    //        });

    //        downloadFile(exportedFile);
    //    } catch (error) {
    //        console.error('Error exporting JSON:', error);
    //    }
    //}

    public saveChanges = (): void => {
        const masterWorkoutExercises = Array.from(this.createdOrEditedMasterWorkoutExerciseVMs).map((vm) => ({ ...vm.masterWorkoutExercise }))
        const deletedMasterWorkoutExerciseIds = Array.from(this.deletedMasterWorkoutExerciseIds)

        this.service
            .submitMasterWorkoutExercises({
                masterWorkoutExercises,
                deletedMasterWorkoutExerciseIds,
                masterWorkoutId: this.masterWorkout.id,
            })
            .then(() => {
                this.createdOrEditedMasterWorkoutExerciseVMs.clear()
                this.deletedMasterWorkoutExerciseIds.clear()
                this.fetch(this.masterWorkout.id)
            })
    }

    public clearCache = (): Promise<any> => this.service.clearMasterWorkoutExercisesCache(this.masterWorkout.id)

    public updateMasterWorkoutExerciseOrder = (items: MasterWorkoutExerciseViewModel[]) => {
        items.forEach((item, index) => {
            item.updateMasterWorkoutExerciseOrder(index)
            this.markForCreateOrEdit(item)
        })
        this.setActiveMasterWorkoutExercise(null)
    }

    public batchEditMasterWorkoutExercises = (values: any): void => {
        const activeWeekVms = this.masterWorkoutExerciseVMs.filter((vm) => vm.masterWorkoutExercise.week == this.activeWeek)
        activeWeekVms.forEach((vm) => {
            vm.applyBatchEdit(values)
            this.markForCreateOrEdit(vm)
        })
    }

    /**
     * ************************************************
     *  Helpers
     * ************************************************
     */

    private reorderAffectedDaysMasterWorkoutExercises = (vms: MasterWorkoutExerciseViewModel[]) => {
        const affectedDays = new Set(vms.map(x => x.masterWorkoutExercise.day))
        Array.from(this.groupByDayMasterWorkoutExercises.entries()).map(([day, values]) => {
            values.filter((x) => affectedDays.has(x.masterWorkoutExercise.day)).forEach((vm, idx) => vm.updateMasterWorkoutExerciseOrder(idx))
        })
    }

    private markForCreateOrEdit = (vm: MasterWorkoutExerciseViewModel) => {
        if (!_.isNil(vm)) this.createdOrEditedMasterWorkoutExerciseVMs.add(vm)
    }
}
